import React from 'react'
import { Pressable, Text, View } from 'react-native'
import { useTranslation } from 'react-i18next'

import { ArrowRightIcon } from '../../../components/ui/ArrowRightIcon'
import { useContentfulLocale } from '../../../hooks/useContentfulLocale'
import { colors } from '../../../theme'
import { body3, body4, label1 } from '../../../theme/typography'
import { type WarmupWeekday } from '../../../utils/daily-warmup-completion'

type WarmupDayStatus = 'completed' | 'missed' | 'future'

interface WarmupDay {
  id: string
  label: string
  status: WarmupDayStatus
  isCurrent?: boolean
}

interface DailyWarmupWeekData {
  days: string[]
  currentDay: string
  isCurrentWeek: boolean
  completedWarmups: string[]
}

interface DailyWarmupCardProps {
  completedWeekdays: WarmupWeekday[]
  isTodayCompleted: boolean
  warmupDurationMinutes: number | null
  onPress?: () => void
  yearWeek: string
}

const DAYS_PER_WEEK = 7

const DAY_CIRCLE_SIZE = 24
const CARD_SURFACE_SHADOW = '0px 2px 4px rgba(0, 0, 0, 0.05)'

const toDateStamp = (date: Date): string => {
  const year = date.getFullYear()
  const month = `${date.getMonth() + 1}`.padStart(2, '0')
  const day = `${date.getDate()}`.padStart(2, '0')

  return `${year}-${month}-${day}`
}

const toDate = (dateValue: string): Date => {
  const [year, month, day] = dateValue.split('-').map(Number)
  return new Date(year, month - 1, day)
}

const addDays = (baseDate: Date, daysToAdd: number): Date => {
  const nextDate = new Date(baseDate)
  nextDate.setDate(nextDate.getDate() + daysToAdd)
  return nextDate
}

const getStartOfIsoWeek = (date: Date): Date => {
  const start = new Date(date.getFullYear(), date.getMonth(), date.getDate())
  const dayOfWeek = start.getDay()
  const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek
  start.setDate(start.getDate() + mondayOffset)

  return start
}

const getStartOfYearWeek = (yearWeek: string): Date => {
  const year = Number(yearWeek.slice(0, 4))
  const week = Number(yearWeek.slice(4, 6))

  const januaryFourth = new Date(year, 0, 4)
  const firstIsoWeekStart = getStartOfIsoWeek(januaryFourth)

  return addDays(firstIsoWeekStart, (week - 1) * DAYS_PER_WEEK)
}

const getWarmupWeekday = (dateValue: string): WarmupWeekday => {
  const dayIndex = toDate(dateValue).getDay()
  if (dayIndex === 0) return 'sunday'
  if (dayIndex === 1) return 'monday'
  if (dayIndex === 2) return 'tuesday'
  if (dayIndex === 3) return 'wednesday'
  if (dayIndex === 4) return 'thursday'
  if (dayIndex === 5) return 'friday'
  return 'saturday'
}

const getIsoWeekdayIndex = (date: Date): number => {
  const dayIndex = date.getDay()
  return dayIndex === 0 ? 6 : dayIndex - 1
}

const resolveWeekData = (
  yearWeek: string,
  completedWeekdays: WarmupWeekday[],
  todayStamp: string,
): DailyWarmupWeekData => {
  const weekStartDate = getStartOfYearWeek(yearWeek)

  const days = Array.from({ length: DAYS_PER_WEEK }, (_, index) =>
    toDateStamp(addDays(weekStartDate, index)),
  )
  const completedWeekdaySet = new Set(completedWeekdays)
  const completedWarmups = days.filter((dateValue) =>
    completedWeekdaySet.has(getWarmupWeekday(dateValue)),
  )
  const currentDay = days[getIsoWeekdayIndex(toDate(todayStamp))]

  return {
    days,
    currentDay,
    isCurrentWeek: days.includes(todayStamp),
    completedWarmups,
  }
}

const toDayLabel = (dateValue: string, locale: string): string => {
  const dayName = toDate(dateValue).toLocaleDateString(locale, {
    weekday: 'short',
  })

  return dayName.slice(0, 1).toUpperCase()
}

const toWarmupDays = (
  weekData: DailyWarmupWeekData,
  todayStamp: string,
  isTodayCompleted: boolean,
  locale: string,
): WarmupDay[] => {
  const completedSet = new Set(weekData.completedWarmups)

  return weekData.days.map((dateValue) => {
    const isCurrent = dateValue === weekData.currentDay
    const isCompleted =
      dateValue === todayStamp ? isTodayCompleted : completedSet.has(dateValue)
    const isPast = dateValue < todayStamp

    let status: WarmupDayStatus = 'future'
    if (isPast) {
      status = isCompleted ? 'completed' : 'missed'
    } else if (isCompleted) {
      status = 'completed'
    }

    return {
      id: dateValue,
      label: toDayLabel(dateValue, locale),
      status,
      isCurrent,
    }
  })
}

const getDayBackgroundColor = (status: WarmupDayStatus): string => {
  if (status === 'completed') return colors.coreSuccess
  if (status === 'missed') return colors.grayMid
  return 'transparent'
}

const getDayBorderColor = (status: WarmupDayStatus): string => {
  if (status === 'future') return colors.grayMid
  if (status === 'completed') return colors.coreSuccess
  return colors.grayMid
}

const getDayTextColor = (status: WarmupDayStatus): string => {
  if (status === 'completed') return colors.primaryForeground
  if (status === 'missed') return colors.textHeader
  return colors.primary
}

export const DailyWarmupCard = ({
  completedWeekdays,
  isTodayCompleted,
  warmupDurationMinutes,
  onPress,
  yearWeek,
}: DailyWarmupCardProps) => {
  const { t } = useTranslation(['dashboard'])
  const locale = useContentfulLocale()
  const todayStamp = toDateStamp(new Date())
  const weekData = resolveWeekData(yearWeek, completedWeekdays, todayStamp)
  const warmupDays = toWarmupDays(
    weekData,
    todayStamp,
    isTodayCompleted,
    locale,
  )
  const showPendingState = weekData.isCurrentWeek && !isTodayCompleted
  const canOpenWarmup = weekData.isCurrentWeek && onPress != null
  const cardTitle = showPendingState
    ? t('dashboard:daily_warmup_title_pending')
    : t('dashboard:daily_warmup_title_completed')

  return (
    <Pressable
      testID="daily-warmup-card"
      disabled={!canOpenWarmup}
      onPress={canOpenWarmup ? onPress : undefined}
      style={{
        marginBottom: 8,
        borderRadius: 8,
        borderWidth: showPendingState ? 1 : 0,
        backgroundColor: showPendingState
          ? colors.secondary700
          : colors.backgroundSecondary,
        borderColor: '#8F8CB2',
        boxShadow: CARD_SURFACE_SHADOW,
      }}
    >
      <View className="pt-4 pb-3 px-4">
        <View
          className="flex-row items-center justify-between"
          style={{ marginBottom: 12 }}
        >
          <View style={{ flex: 1, paddingRight: 16 }}>
            <Text
              className=""
              style={{
                ...label1,
                color: colors.textHeader,
              }}
            >
              {cardTitle}
            </Text>
            {showPendingState && warmupDurationMinutes != null && (
              <Text
                className=""
                style={{
                  ...body3,
                  color: colors.textHeader,
                  marginTop: 6,
                }}
              >
                {warmupDurationMinutes} {t('dashboard:minutes_short')}
              </Text>
            )}
          </View>
          {canOpenWarmup && <ArrowRightIcon color={colors.textHeader} />}
        </View>

        <View className="flex-row items-start justify-between">
          {warmupDays.map((day) => (
            <View key={day.id} className="items-center">
              <View
                className="items-center justify-center rounded-full"
                style={{
                  width: DAY_CIRCLE_SIZE,
                  height: DAY_CIRCLE_SIZE,
                  backgroundColor: getDayBackgroundColor(day.status),
                  borderColor: getDayBorderColor(day.status),
                  borderWidth: 2,
                }}
              >
                <Text
                  className=""
                  style={{
                    ...body4,
                    color: getDayTextColor(day.status),
                  }}
                >
                  {day.label}
                </Text>
              </View>
              {day.isCurrent && (
                <View
                  testID={`warmup-current-day-indicator-${day.id}`}
                  className="mt-1 h-1 w-1 rounded-full"
                  style={{ backgroundColor: colors.primary }}
                />
              )}
            </View>
          ))}
        </View>
      </View>
    </Pressable>
  )
}
